// src/Dashboard/pages/user/Galleries/GalleriesPhotosDashboard.jsx

import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";

import { useGalleriesPhotos } from "./hooks/useGalleriesPhotos";

import GalleriesHeader from "./components/GalleriesHeader";
import GalleriesKpis from "./components/GalleriesKpis";
import AlbumsToolbar from "./components/AlbumsToolbar";
import GalleryCard from "./components/GalleryCard";
import GallerySkeleton from "./components/GallerySkeleton";
import GalleriesEmptyState from "./components/GalleriesEmptyState";
import AlbumDrawer from "./components/AlbumDrawer";

export default function GalleriesPhotosDashboard() {
  useScrollToTop();

  const {
    userRole,
    isManager,

    galleries,
    filtered,
    loading,
    error,
    stats,

    ui,
    debouncedQ,
    setDebouncedQ,
    updateUi,
    resetFilters,

    drawerMode,
    selectedGallery,
    albumDrawerOpen,
    openCreateDrawer,
    openEditDrawer,
    closeAlbumDrawer,
    handleAlbumSaved,

    handleDeleteGallery,
    refresh,
  } = useGalleriesPhotos();

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <GalleriesHeader
          loading={loading}
          isManager={isManager}
          onRefresh={refresh}
          onCreate={openCreateDrawer}
        />

        <GalleriesKpis stats={stats} loading={loading} isManager={isManager} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Souvenirs du club
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Albums photos
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Retrouvez les photos des événements, compétitions, stages et
                  temps forts du club. Les albums peuvent être filtrés, triés et
                  administrés selon votre rôle.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Affichés :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filtered.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {galleries.length}
                </span>
              </div>
            </div>

            <div className="mt-5">
              <AlbumsToolbar
                ui={ui}
                updateUi={updateUi}
                debouncedQ={debouncedQ}
                setDebouncedQ={setDebouncedQ}
                isManager={isManager}
                onReset={resetFilters}
                onCreate={openCreateDrawer}
              />
            </div>

            {loading ? (
              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <GallerySkeleton key={index} />
                ))}
              </div>
            ) : error ? (
              <div className="mt-5 rounded-[24px] border border-red-200 bg-red-50 px-5 py-8 text-center dark:border-red-500/20 dark:bg-red-500/10">
                <p className="text-sm font-bold text-red-700 dark:text-red-300">
                  {error}
                </p>

                <button
                  type="button"
                  onClick={refresh}
                  className="mt-4 rounded-2xl bg-red-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-red-700"
                >
                  Réessayer
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <GalleriesEmptyState
                isManager={isManager}
                onReset={resetFilters}
                onCreate={openCreateDrawer}
              />
            ) : (
              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {filtered.map((gallery, index) => (
                  <GalleryCard
                    key={gallery.id}
                    gallery={gallery}
                    index={index}
                    userRole={userRole}
                    isManager={isManager}
                    onEdit={() => openEditDrawer(gallery)}
                    onDelete={() => handleDeleteGallery(gallery)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      <AlbumDrawer
        isOpen={albumDrawerOpen}
        mode={drawerMode}
        gallery={selectedGallery}
        onClose={closeAlbumDrawer}
        onSaved={handleAlbumSaved}
      />

      <ToastContainer />
    </section>
  );
}
